import React, { useState } from 'react';
import { Plus, MapPin } from 'lucide-react';
import useAppStore from '../store';
import MapComponent from '../components/Map';
import { Depot } from '../types';

const DepotsPage: React.FC = () => {
  const { depots, addDepot, currentUser } = useAppStore();
  const [showAddModal, setShowAddModal] = useState(false);
  
  // New depot form state
  const [newDepot, setNewDepot] = useState({
    name: '',
    lat: '',
    lng: '',
  });
  
  // Handle form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewDepot(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await addDepot({
        id: `depot-${Date.now()}`,
        name: newDepot.name,
        position: {
          lat: parseFloat(newDepot.lat),
          lng: parseFloat(newDepot.lng),
        },
      });
      
      // Reset form and close modal
      setNewDepot({
        name: '',
        lat: '',
        lng: '',
      });
      
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding depot:', error);
      alert('Une erreur est survenue lors de la création du dépôt');
    }
  };
  
  // Check if user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <h2 className="text-xl font-bold mb-2">Accès refusé</h2>
        <p>Vous n'avez pas les droits nécessaires pour accéder à cette page.</p>
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="bg-white rounded-lg shadow mb-4 p-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Liste des dépôts ({depots.length})</h2>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-800 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un dépôt
          </button>
        </div>
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {depots.map((depot: Depot) => (
                <tr key={depot.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="bg-gray-100 text-gray-800 rounded-full h-8 w-8 flex items-center justify-center mr-3">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div className="font-medium">{depot.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {depot.position.lat.toFixed(6)}, {depot.position.lng.toFixed(6)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-xl font-bold mb-4">Carte des dépôts</h2>
        <div className="h-96">
          <MapComponent showDrivers={false} dumpsters={[]} />
        </div>
      </div>
      
      {/* Add Depot Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Ajouter un dépôt</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  Nom du dépôt
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newDepot.name}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="lat" className="block text-gray-700 font-medium mb-2">
                    Latitude
                  </label>
                  <input
                    type="text"
                    id="lat"
                    name="lat"
                    value={newDepot.lat}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    pattern="-?\d+(\.\d+)?"
                    placeholder="Ex: 48.8566"
                  />
                </div>
                
                <div>
                  <label htmlFor="lng" className="block text-gray-700 font-medium mb-2">
                    Longitude
                  </label>
                  <input
                    type="text"
                    id="lng"
                    name="lng"
                    value={newDepot.lng}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    pattern="-?\d+(\.\d+)?"
                    placeholder="Ex: 2.3522"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepotsPage;